//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { EnhancedInput } from 'common/components/core/input';
import RegExUtil from 'common/utils/RegExUtil';
import { EditableInputField } from 'common/components/core/common/CustomEditableField';
import { Button, ButtonTypes } from 'common/components/core/button';
import { withRouter } from 'common/components/core/router';
import queryString from 'query-string';
import { dashboardColors } from 'common/enums/ColorEnum';
import { DataUtils } from 'common/utils/DataUtils';
import defaultTheme from './CodeInputWithId.scss';
import Icon from 'common/components/core/icon';
import { translate } from 'react-i18next';
import debounce from 'lodash/debounce';
import { ApiError } from 'common/modules/api';
import entityIds from 'common/tests/entity/entityIds.json';

type Props = {
    theme: Object,
    id: string,
    placeholder: string,
    label?: string,
    error: string,
    code: string,
    disableDuplicateConfirmation?: boolean
};
export const ValidationStatusEnum = {
    Default: 'Default', // reset back to this on mount/unmount
    Loading: 'Loading',
    DuplicateConfimation: 'DuplicateConfimation', // this state can be skipped based on a prop
    Error: 'Error',
    NewId: 'NewId'
};

class CodeInputWithId extends React.Component<Props> {
    constructor(props) {
        super(props);
        this.state = {
            validationStatus: ValidationStatusEnum.Default,
            updatedEntity: props.entity,
            searchValue: null
        };
        this.debouncedValidateDuplicateIdCall = debounce(
            this.validateDuplicateIdCall,
            300
        );
    }

    renderId(theme) {
        const { value, id, t } = this.props;
        return (
            <div className={theme.idLabel}>
                {t('masterEntityProperties.id')}:{' '}
                {!DataUtils.isEmpty(id)
                    ? id
                    : RegExUtil.conformToId(id || value || '')}
            </div>
        );
    }

    editEntity = () => {
        const { location, history, id } = this.props;
        const searchQuery = queryString.stringify({
            edit: id
        });
        history.push({
            ...location,
            search: searchQuery
        });
    };

    dismissErrorMessage = () => {
        const { t } = this.props;
        this.setState({
            errorMessage: t('changeIdError'),
            validationStatus: ValidationStatusEnum.Error
        });
    };

    renderDuplicateIdError() {
        const { theme, t } = this.props;
        return (
            <div className={theme.codeDuplicateError}>
                <span
                    className={theme.warningText}
                    data-test-id={entityIds.warnings.existingId}
                >
                    {t('editExistingId')}
                </span>
                <div className={theme.codeDuplicateButtons}>
                    <Button
                        theme={theme}
                        color={dashboardColors.darkSkyBlue.name}
                        buttonType={ButtonTypes.Link}
                        semibold
                        uppercase
                        label={t('translations:yes')}
                        onClick={this.editEntity}
                        testId={entityIds.buttons.updateExistingId}
                    />
                    <Button
                        theme={theme}
                        buttonType={ButtonTypes.Link}
                        semibold
                        uppercase
                        label={t('translations:no')}
                        onClick={this.dismissErrorMessage}
                        testId={entityIds.buttons.cancelUpdateId}
                    />
                </div>
            </div>
        );
    }

    validateDuplicateIdCall = value => {
        const {
            clientId,
            validateDuplicateId,
            onValidateDuplicateIdChange,
            entity,
            disableDuplicateConfirmation,
            conflictEntities,
            t
        } = this.props;
        if (
            DataUtils.isEmpty(entity.id) ||
            entity.getValidationMessage('code')
        ) {
            this.setState({
                validationStatus: ValidationStatusEnum.Error,
                errorMessage: null
            });
            return Promise.resolve();
        }
        onValidateDuplicateIdChange(ValidationStatusEnum.Loading);
        this.setState({ validationStatus: ValidationStatusEnum.Loading });
        return validateDuplicateId(
            { clientId },
            {
                entityIds: [value]
            }
        )
            .then(response => {
                /* this check is to filter
                out the Promises and resolve the promise invoked by the latest search text only */
                if (this.state.searchValue === value) {
                    if (response.entityIds.length) {
                        // if duplicate found
                        if (!disableDuplicateConfirmation) {
                            this.setState({
                                validationStatus:
                                    ValidationStatusEnum.DuplicateConfimation
                            });
                        } else {
                            this.setState({
                                errorMessage: t('changeIdError'),
                                validationStatus: ValidationStatusEnum.Error
                            });
                        }
                        onValidateDuplicateIdChange(
                            ValidationStatusEnum.DuplicateConfimation
                        );
                    } else if (response.reservedIds.length) {
                        this.setState({
                            errorMessage: t('conflictIdError', {
                                conflictEntities
                            }),
                            validationStatus: ValidationStatusEnum.Error
                        });
                    } else {
                        this.setState({
                            validationStatus: ValidationStatusEnum.NewId
                        });
                        onValidateDuplicateIdChange(ValidationStatusEnum.NewId);
                    }
                }
            })
            .catch(error => {
                if (this.state.searchValue === value) {
                    if (error instanceof ApiError) {
                        this.setState({
                            errorMessage: t('genericError'),
                            validationStatus: ValidationStatusEnum.Error
                        });
                        onValidateDuplicateIdChange(
                            ValidationStatusEnum.Default
                        );
                        return;
                    }
                    throw error;
                }
            });
    };

    triggerIdValidation = id => {
        const conformId = RegExUtil.conformToId(id);
        this.setState({
            validationStatus: ValidationStatusEnum.Default
        });
        this.setState({ searchValue: conformId });
        this.debouncedValidateDuplicateIdCall(conformId);
    };

    onIdSave = value => {
        const { onChange, entity } = this.props;
        const conformId = RegExUtil.conformToId(value);
        onChange && onChange({ id: value });
        this.setState({
            searchValue: conformId,
            updatedEntity: entity.setId(value)
        });
        return this.validateDuplicateIdCall(conformId);
    };

    handleIdChange = value => {
        const { entity } = this.props;
        this.setState({
            updatedEntity: entity.setId(value)
        });
    };

    handleCodeChange = value => {
        const { onChange, entity } = this.props;
        const conformId = RegExUtil.conformToId(value);
        onChange && onChange({ code: value });
        this.setState({
            updatedEntity: entity.setCode?.(value) || entity.setId(value)
        });
        this.triggerIdValidation(conformId);
    };

    render() {
        const {
            theme,
            placeholder,
            code,
            id,
            disabled,
            entity,
            t,
            testId
        } = this.props;
        const { validationStatus, updatedEntity, errorMessage } = this.state;
        const idValidationError =
            validationStatus === ValidationStatusEnum.Error && errorMessage;
        const currentId = !DataUtils.isEmpty(id) ? id : code;
        const error =
            !disabled &&
            (entity.getValidationMessage('code') || idValidationError);
        return (
            <div className={theme.codeIdInputWrap}>
                <EnhancedInput
                    testId={testId}
                    theme={theme}
                    placeholder={placeholder}
                    disabled={disabled}
                    error={error}
                    value={code || ''}
                    onChange={this.handleCodeChange}
                    isInputLoading={
                        validationStatus === ValidationStatusEnum.Loading
                    }
                    rightIcon={
                        validationStatus === ValidationStatusEnum.NewId && (
                            <Icon
                                theme={theme}
                                className={theme.newIdIcon}
                                circular
                                wrapBgColor={dashboardColors.coolGreen.name}
                                icon="tickmark"
                            />
                        )
                    }
                />

                {!DataUtils.isEmpty(currentId) ? (
                    <div className={theme.idWrap}>
                        <span>
                            {t('masterEntityProperties.id')}
                            &nbsp;:
                        </span>
                        <EditableInputField
                            isLoading={
                                validationStatus ===
                                ValidationStatusEnum.Loading
                            }
                            theme={theme}
                            disableSaveOnError
                            type="text"
                            onChange={this.handleIdChange}
                            value={currentId}
                            isEditingEnabled={!disabled}
                            onSave={this.onIdSave}
                            autoSaveOnChange={false}
                            className={theme.editableIdField}
                            errorMessage={updatedEntity.getValidationMessage(
                                'id'
                            )}
                        />
                    </div>
                ) : null}

                {!disabled &&
                validationStatus ===
                    ValidationStatusEnum.DuplicateConfimation &&
                !DataUtils.isEmpty(currentId)
                    ? this.renderDuplicateIdError()
                    : null}
            </div>
        );
    }
}
const WrappedCodeInputWithId = translate('entityManagement')(
    themr('CodeInputWithId', defaultTheme)(withRouter(CodeInputWithId))
);
export default WrappedCodeInputWithId;
